import { WORKFLOW_RESUME_ACTION_PREFIX, type GencowCtx, type GencowProcedureDef } from "@gencow/core";
import type { FunctionType } from "./server-execution.js";
import {
  executeScheduledAction,
  ScheduledActionNotFoundError,
  type ScheduledActionMaps,
} from "./scheduled-action-resolver.js";

type LoggerLike = Pick<Console, "log" | "warn" | "error">;

type MutationHandler = (ctx: GencowCtx, args: unknown) => Promise<unknown>;

type MutationEntry = {
  argsSchema?: unknown;
  handler: MutationHandler;
};

type ProcedureInternalEntry = GencowProcedureDef<"internal", any, any, any>;
type ProcedureMutationEntry = GencowProcedureDef<"mutation", any, any, any>;

type SchedulerLike = {
  executeActionStrict(action: string, args: unknown): Promise<unknown>;
};

type RouteContext = {
  req: {
    param(name: string): string;
    header(name: string): string | undefined;
    json(): Promise<unknown>;
  };
  json(body: unknown, status?: number): unknown;
};

type RouteRegistrar = {
  post(path: string, handler: (context: RouteContext) => Promise<unknown> | unknown): void;
};

type ExecuteWithTimeout = <T>(fn: () => Promise<T>, kind: FunctionType, name: string) => Promise<T>;

export function isAuthorizedCronToken(
  token: string | undefined,
  internalToken: string | undefined,
  cronToken: string | undefined,
): boolean {
  if (!token) return false;
  return (Boolean(internalToken) && token === internalToken) || (Boolean(cronToken) && token === cronToken);
}

function canFallbackToStableSchedulerAction(action: string): boolean {
  return action.startsWith(`${WORKFLOW_RESUME_ACTION_PREFIX}.`);
}

export function createInternalCronRouteHandler(params: {
  mutationMap: Map<string, MutationEntry>;
  internalProcedureMap?: Map<string, ProcedureInternalEntry>;
  mutationProcedureMap?: Map<string, ProcedureMutationEntry>;
  actionTokenMap?: Map<string, string>;
  scheduler?: SchedulerLike;
  buildCtx: () => GencowCtx;
  parseArgs?: (schema: unknown, args: unknown) => unknown;
  executeWithTimeout: ExecuteWithTimeout;
  internalToken?: string;
  cronToken?: string;
  logger?: LoggerLike;
}): (context: RouteContext) => Promise<unknown> {
  const logger = params.logger ?? console;

  return async (c) => {
    const action = c.req.param("action");
    const token = c.req.header("X-Cron-Token");

    if (!isAuthorizedCronToken(token, params.internalToken, params.cronToken)) {
      logger.warn(`[cron] unauthorized /internal/cron/${action} — invalid X-Cron-Token`);
      return c.json({ ok: false, error: "Unauthorized" }, 403);
    }

    const body = (await c.req.json().catch(() => ({}))) as { args?: unknown };
    const hasActionTokenMap = !!params.actionTokenMap && params.actionTokenMap.size > 0;
    const maps: ScheduledActionMaps = {
      internalProcedureMap: params.internalProcedureMap ?? new Map(),
      mutationProcedureMap: params.mutationProcedureMap ?? new Map(),
      mutationMap: params.mutationMap,
    };

    try {
      await executeScheduledAction({
        action,
        rawArgs: body.args ?? {},
        actionTokenMap: params.actionTokenMap,
        maps,
        buildCtx: params.buildCtx,
        parseArgs: params.parseArgs,
        executeWithTimeout: params.executeWithTimeout,
        invoker: "cron",
        allowWireActionNameFallback: !hasActionTokenMap,
      });
      logger.log(`[cron] /internal/cron/${action}: completed ✓`);
      return c.json({ ok: true });
    } catch (error: unknown) {
      if (!(error instanceof ScheduledActionNotFoundError)) {
        const message = error instanceof Error ? error.message : String(error);
        logger.error(`[cron] /internal/cron/${action}: failed:`, message);
        return c.json({ ok: false, error: message }, 500);
      }
      if (hasActionTokenMap && !canFallbackToStableSchedulerAction(action)) {
        logger.warn(`[cron] /internal/cron/${action}: action token not found`);
        return c.json({ ok: false, error: `Action "${action}" not found` }, 404);
      }

      if (!params.scheduler) {
        logger.warn(`[cron] /internal/cron/${action}: action not found and scheduler unavailable`);
        return c.json({ ok: false, error: `Action "${action}" not found` }, 404);
      }

      try {
        await params.executeWithTimeout(
          () => params.scheduler!.executeActionStrict(action, body.args ?? {}),
          "cron",
          action,
        );
        logger.log(`[cron] /internal/cron/${action}: scheduler action completed ✓`);
        return c.json({ ok: true });
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        logger.error(`[cron] /internal/cron/${action}: scheduler action failed:`, message);
        return c.json({ ok: false, error: message }, 500);
      }
    }
  };
}

export function registerInternalCronRoute(params: {
  app: RouteRegistrar;
  mutationMap: Map<string, MutationEntry>;
  internalProcedureMap?: Map<string, ProcedureInternalEntry>;
  mutationProcedureMap?: Map<string, ProcedureMutationEntry>;
  actionTokenMap?: Map<string, string>;
  scheduler?: SchedulerLike;
  buildCtx: () => GencowCtx;
  parseArgs?: (schema: unknown, args: unknown) => unknown;
  executeWithTimeout: ExecuteWithTimeout;
  internalToken?: string;
  cronToken?: string;
  logger?: LoggerLike;
}): void {
  const logger = params.logger ?? console;
  params.app.post(
    "/internal/cron/:action",
    createInternalCronRouteHandler({
      mutationMap: params.mutationMap,
      internalProcedureMap: params.internalProcedureMap,
      mutationProcedureMap: params.mutationProcedureMap,
      actionTokenMap: params.actionTokenMap,
      scheduler: params.scheduler,
      buildCtx: params.buildCtx,
      parseArgs: params.parseArgs,
      executeWithTimeout: params.executeWithTimeout,
      internalToken: params.internalToken,
      cronToken: params.cronToken,
      logger,
    }),
  );
  logger.log("[gencow] /internal/cron/:action endpoint registered (BaaS mode)");
}
